import { css } from '@emotion/react';

import { getConfig } from '@common/config';

const config = getConfig();

// Typography 설정
const TYPOGRAPHY_CONFIG = {
  HEAD: [
    { fontSize: 28, lineHeight: 34, multilineHeight: 38 },
    { fontSize: 24, lineHeight: 29, multilineHeight: 32 },
    { fontSize: 22, lineHeight: 26, multilineHeight: 30 },
    { fontSize: 20, lineHeight: 24, multilineHeight: 28 },
  ],
  BODY: [
    { fontSize: 18, lineHeight: 22, multilineHeight: 26 },
    { fontSize: 17, lineHeight: 20, multilineHeight: 24 },
    { fontSize: 16, lineHeight: 19, multilineHeight: 24 },
    { fontSize: 15, lineHeight: 18, multilineHeight: 22 },
    { fontSize: 14, lineHeight: 17, multilineHeight: 20 },
    { fontSize: 13, lineHeight: 16, multilineHeight: 18 },
  ],
  CAPTION: [
    { fontSize: 12, lineHeight: 14, multilineHeight: 16 },
    { fontSize: 11, lineHeight: 13, multilineHeight: 14 },
    { fontSize: 10, lineHeight: 12, multilineHeight: 14 },
    { fontSize: 9, lineHeight: 11, multilineHeight: 12 },
  ],
} as const;

const FONT_WEIGHTS = {
  BOLD: 700,
  SEMIBOLD: 600,
  MEDIUM: 500,
  REGULAR: 400,
} as const;

// Typography 클래스 생성
const generateTypographyClasses = () => {
  const classes: string[] = [];

  Object.entries(TYPOGRAPHY_CONFIG).forEach(([category, configs]) => {
    configs.forEach(({ fontSize, lineHeight, multilineHeight }) => {
      // Template Literal 여백때문에 indent 앞으로 땡김
      classes.push(`.${category}_${fontSize} {
  font-size: ${fontSize}px;
  line-height: ${lineHeight}px;

  &.MULTILINE {
    line-height: ${multilineHeight}px;
  }
}`);
    });
  });

  return classes.join('\n');
};

// 모든 Typography 클래스 선택자 생성
const generateTypographySelectors = () => {
  const selectors: string[] = [];

  Object.entries(TYPOGRAPHY_CONFIG).forEach(([category, configs]) => {
    configs.forEach(({ fontSize }) => {
      selectors.push(`.${category}_${fontSize}`);
    });
  });

  return selectors.join(',\n');
};

// Font weight 수정자 생성
const generateFontWeightModifiers = () => {
  return Object.entries(FONT_WEIGHTS)
    .map(([name, weight]) => {
      // Template Literal 여백때문에 indent 앞으로 땡김
      return `&.${name} {
  font-weight: ${weight};
}`;
    })
    .join('\n');
};

export const font = css`
  @font-face {
    font-family: Pretendard;
    src:
      local('Pretendard-Regular'),
      url('${config.resUrl}/fonts/Pretendard/Pretendard-Regular.subset.woff2') format('woff2'),
      url('${config.resUrl}/fonts/Pretendard/Pretendard-Regular.woff2') format('woff2'),
      url('${config.resUrl}/fonts/Pretendard/Pretendard-Regular.subset.woff') format('woff'),
      url('${config.resUrl}/fonts/Pretendard/Pretendard-Regular.woff') format('woff');
    font-weight: 400;
    font-display: swap;
  }
  @font-face {
    font-family: Pretendard;
    src:
      local('Pretendard-Medium'),
      url('${config.resUrl}/fonts/Pretendard/Pretendard-Medium.subset.woff2') format('woff2'),
      url('${config.resUrl}/fonts/Pretendard/Pretendard-Medium.woff2') format('woff2'),
      url('${config.resUrl}/fonts/Pretendard/Pretendard-Medium.subset.woff') format('woff'),
      url('${config.resUrl}/fonts/Pretendard/Pretendard-Medium.woff') format('woff');
    font-weight: 500;
    font-display: swap;
  }
  @font-face {
    font-family: Pretendard;
    src:
      local('Pretendard-SemiBold'),
      url('${config.resUrl}/fonts/Pretendard/Pretendard-SemiBold.subset.woff2') format('woff2'),
      url('${config.resUrl}/fonts/Pretendard/Pretendard-SemiBold.woff2') format('woff2'),
      url('${config.resUrl}/fonts/Pretendard/Pretendard-SemiBold.subset.woff') format('woff'),
      url('${config.resUrl}/fonts/Pretendard/Pretendard-SemiBold.woff') format('woff');
    font-weight: 600;
    font-display: swap;
  }
  @font-face {
    font-family: Pretendard;
    src:
      local('Pretendard-Bold'),
      url('${config.resUrl}/fonts/Pretendard/Pretendard-Bold.subset.woff2') format('woff2'),
      url('${config.resUrl}/fonts/Pretendard/Pretendard-Bold.woff2') format('woff2'),
      url('${config.resUrl}/fonts/Pretendard/Pretendard-Bold.subset.woff') format('woff'),
      url('${config.resUrl}/fonts/Pretendard/Pretendard-Bold.woff') format('woff');
    font-weight: 700;
    font-display: swap;
  }

  ${generateTypographyClasses()}

  ${generateTypographySelectors()} {
    ${generateFontWeightModifiers()}
  }
`;
